/**
 * `SyncStatusBadge` (WP-1).
 *
 * Header badge that surfaces the gTwin's sync status (`clean | dirty |
 * saving | failed`) per the design-policy state-matrix rule §5: every
 * state pairs colour with a non-colour cue. Colour is sourced from
 * `tokens.color.semantic.{success|warning|info|danger}`; the non-colour
 * cue is a Material Symbols Rounded icon plus a textual label rendered
 * inside a `role="status"` polite live region so AT users hear the
 * transition.
 *
 * Reads from `useSyncStatusStore` and is fully tokenized.
 */

import { tokens, Icon, type IconName } from '@/design';
import {
  useSyncStatusStore,
  type GtwinSyncState,
} from '@/state/useSyncStatusStore';

interface BadgeFace {
  readonly icon: IconName;
  readonly label: string;
  readonly accent: string;
  readonly description: string;
}

const FACE_BY_STATE: Record<GtwinSyncState, BadgeFace> = {
  clean: {
    icon: 'check_circle',
    label: 'Saved',
    accent: tokens.color.semantic.success,
    description: 'All scene edits are saved to the gTwin.',
  },
  dirty: {
    icon: 'edit',
    label: 'Unsaved changes',
    accent: tokens.color.semantic.warning,
    description: 'Pending scene edits not yet written to the gTwin.',
  },
  saving: {
    icon: 'sync_alt',
    label: 'Saving…',
    accent: tokens.color.semantic.info,
    description: 'Writing scene edits to the gTwin.',
  },
  failed: {
    icon: 'error',
    label: 'Save failed',
    accent: tokens.color.semantic.danger,
    description: 'Last save attempt failed; click to retry.',
  },
};

export interface SyncStatusBadgeProps {
  /** Optional override for the textual label (used by tests). */
  readonly forcedState?: GtwinSyncState;
  /** Optional override for the detail text (used by tests; bypasses store reads). */
  readonly forcedDetail?: string;
  /** Optional click handler used by the failed-state retry affordance. */
  readonly onRetry?: () => void;
  readonly 'data-testid'?: string;
}

export function SyncStatusBadge({
  forcedState,
  forcedDetail,
  onRetry,
  'data-testid': testId,
}: SyncStatusBadgeProps) {
  const status = useSyncStatusStore((s) => s.status);
  const lastSavedAt = useSyncStatusStore((s) => s.lastSavedAt);
  const lastErrorMessage = useSyncStatusStore((s) => s.lastErrorMessage);
  const pending = useSyncStatusStore((s) => s.pendingMutationCount);

  const effective: GtwinSyncState = forcedState ?? status;
  const face = FACE_BY_STATE[effective];

  const detail = (() => {
    if (forcedDetail !== undefined) return forcedDetail;
    if (effective === 'failed' && lastErrorMessage) return lastErrorMessage;
    if (effective === 'saving' && pending > 0) return `${pending} pending`;
    if (effective === 'clean' && lastSavedAt) {
      try {
        return `Saved ${new Date(lastSavedAt).toLocaleTimeString()}`;
      } catch {
        return undefined;
      }
    }
    return undefined;
  })();

  const Wrapper = effective === 'failed' && onRetry ? 'button' : 'div';

  return (
    <Wrapper
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid={testId ?? 'sync-status-badge'}
      data-sync-state={effective}
      onClick={effective === 'failed' && onRetry ? onRetry : undefined}
      title={detail ? `${face.description} — ${detail}` : face.description}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[2],
        padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
        borderRadius: tokens.radius.pill,
        background: tokens.color.surface[2],
        border: tokens.border.thin,
        borderLeft: `${tokens.border.width.thin * 3}px solid ${face.accent}`,
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        fontSize: tokens.font.size.sm,
        cursor: effective === 'failed' && onRetry ? 'pointer' : 'default',
        minHeight: tokens.density.touchMin,
      }}
    >
      <Icon
        name={face.icon}
        size={20}
        decorative
        style={{ color: face.accent }}
      />
      <span style={{ fontWeight: tokens.font.weight.medium }}>{face.label}</span>
      {detail ? (
        <span
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.xs,
            fontFamily:
              effective === 'clean' || effective === 'saving'
                ? tokens.font.family.mono
                : tokens.font.family.ui,
          }}
        >
          {detail}
        </span>
      ) : null}
    </Wrapper>
  );
}
